// Industry starters for quotations (and any other document). A preset fills the
// subject, typical scope lines, notes and terms – nothing here is tied to one
// business: "{company}" becomes the chosen business profile's name and
// "{validity}" the number of days the offer is valid for. Prices, HSN/SAC codes and
// tax rates are left to the user (their defaults and saved services fill them in).

// Document options a business type usually needs. Each one adds a column or a block
// to the document; anything not listed stays hidden for that business (it can still
// be switched on per document).
//   sections – group lines under headings with subtotals (e.g. Supply / Installation)
//   optional – optional add-ons the client can choose, not in the total
//   photos   – a product photo on each line
//   discount – discount % per line
//   inclusive – prices typed including tax (B2C), taxable value worked out
//   schedule – payment stages (advance / on delivery …)
export const FEATURES = {
  sections: { label: "Sections", hint: "Group lines under headings with subtotals" },
  optional: { label: "Optional items", hint: "Add-ons shown on the quote but not in the total until the client chooses them" },
  photos: { label: "Product photos", hint: "A small photo on each line" },
  discount: { label: "Line discount", hint: "Discount % on individual lines" },
  inclusive: { label: "Prices include tax", hint: "Type prices with GST/VAT included – the taxable value is worked out" },
  schedule: { label: "Payment schedule", hint: "Payment stages with amounts, and an invoice for each stage" },
};

const COMMON_CLOSING =
  "This quotation is an indicative offer based on the requirements shared with us. Prices may change if the scope, quantities or specifications change.";

export const INDUSTRY_PRESETS = [
  {
    id: "general",
    features: ["optional", "discount", "schedule"],
    schedule: [["Advance with order", 50], ["On delivery / completion", 50]],
    label: "General business",
    icon: "🏢",
    desc: "Any product or service – a clean starting point",
    subject: "Quotation for the requested products / services",
    validityDays: 15,
    items: [
      { description: "Product / service", details: "Brief specification or scope", qty: 1 },
    ],
    notes: "Thank you for the opportunity. We look forward to working with you.",
    terms: [
      "Prices are valid for {validity} days from the date of this quotation.",
      "Taxes are charged as shown; any change in tax rates at the time of billing will apply.",
      "Payment: 50% advance with the order, balance on delivery / completion.",
      "Delivery / completion timeline will be confirmed on receipt of the order.",
      COMMON_CLOSING,
    ],
  },
  {
    id: "solar",
    features: ["sections", "optional", "inclusive", "schedule"],
    schedule: [["Advance with purchase order", 25], ["Before dispatch of material", 70], ["On installation & commissioning", 5]],
    label: "Solar & renewable energy",
    icon: "☀️",
    desc: "Rooftop / on-grid / off-grid systems, EPC with installation",
    subject: "Quotation for supply and installation of a rooftop solar power system",
    validityDays: 12,
    template: "standard",
    items: [
      {
        description: "On-grid solar power system",
        section: "Supply",
        details: "Solar modules, inverter, ACDB + DCDB, earthing kit\nAC and DC cables\nNet meter / solar meter\nGI mounting structure",
        qty: 1,
      },
      { section: "Installation", description: "Installation & commissioning", details: "Site survey, installation, testing and DISCOM coordination", qty: 1 },
    ],
    notes: "Products carry the manufacturers' warranties. Free maintenance visits as per the after-sales terms below.",
    terms: [
      "Scope of work: supply, installation and commissioning of the solar PV system as per the agreed specifications, including coordination with the electricity distribution company for approvals and net-metering.",
      "Payment schedule: 25% advance with the purchase order, 70% before dispatch of material, 5% on installation and commissioning.",
      "Overdue payments attract interest at 18% per annum until cleared.",
      "Timeline: completion schedule to be mutually agreed at order finalisation. Packing and delivery to site are included.",
      "Inspections by the client or a third party are not included unless agreed in writing.",
      "After-sales: complimentary maintenance as agreed (excluding panel cleaning); thereafter O&M charges as per a separate contract.",
      "Any change to system design or scope may change the quoted price.",
      "Ownership of all equipment stays with {company} until full payment is received.",
      "Warranty on products as provided by their manufacturers.",
      "Force majeure: {company} is not responsible for delays caused by events beyond its control.",
      COMMON_CLOSING,
    ],
  },
  {
    id: "it",
    features: ["optional", "schedule"],
    schedule: [["Advance", 40], ["On design / beta approval", 40], ["Before go-live", 20]],
    label: "IT & software",
    icon: "💻",
    desc: "Websites, apps, software development, hosting and support",
    subject: "Proposal for website / software development",
    validityDays: 30,
    template: "executive",
    items: [
      { description: "Discovery & UI/UX design", details: "Requirement workshops, wireframes and visual design", qty: 1 },
      { description: "Development", details: "Front-end and back-end development, integrations and testing", qty: 1 },
      { description: "Deployment & training", details: "Go-live, handover documentation and user training", qty: 1 },
      { optional: true, description: "Annual hosting & support", details: "Hosting, SSL, backups and up to 5 hours of changes per month", qty: 12 },
    ],
    notes: "Timelines start from the receipt of the advance and all required content / access.",
    terms: [
      "Payment: 40% advance, 40% on design / beta approval, 20% before go-live.",
      "The scope is limited to the items listed. New features or changes are quoted separately.",
      "Two rounds of design revisions are included.",
      "Source code and intellectual property transfer to the client on full payment.",
      "Third-party costs (domains, paid plugins, APIs, app-store fees) are not included unless listed.",
      "Support covers bug fixes for 30 days after go-live.",
      "This proposal is valid for {validity} days.",
    ],
  },
  {
    id: "marketing",
    features: ["optional", "discount"],
    label: "Digital marketing & creative",
    icon: "📣",
    desc: "SEO, ads, social media, branding and content retainers",
    subject: "Proposal for digital marketing services",
    validityDays: 15,
    template: "modern",
    items: [
      { description: "SEO – monthly retainer", details: "Keyword research, on-page optimisation, content and link building, monthly report", qty: 1 },
      { description: "Social media management – monthly", details: "12 posts / month across 2 platforms, community management", qty: 1 },
      { description: "Paid ads management – monthly", details: "Campaign setup and optimisation (ad spend billed separately)", qty: 1 },
    ],
    notes: "Ad budgets are paid directly to the ad platforms and are not part of this quotation.",
    terms: [
      "Retainers are billed monthly in advance; minimum engagement of 3 months.",
      "Results depend on market competition and platform algorithms; no specific ranking or sales figures are guaranteed.",
      "The client provides timely approvals, brand assets and website access.",
      "Either party may end the engagement with 30 days' written notice.",
      "Prices are valid for {validity} days.",
    ],
  },
  {
    id: "construction",
    features: ["sections", "schedule"],
    schedule: [["Mobilisation advance", 20], ["Running bills (work done)", 75], ["After defect liability period", 5]],
    label: "Construction & civil works",
    icon: "🏗️",
    desc: "Building, renovation, civil and structural works",
    subject: "Quotation for civil and construction works",
    validityDays: 30,
    template: "standard",
    items: [
      { section: "Civil works", description: "Earthwork & excavation", details: "Excavation in all soils including disposal of surplus earth", qty: 1 },
      { section: "Civil works", description: "RCC work", details: "Concrete M20 including shuttering and reinforcement", qty: 1 },
      { section: "Masonry & finishing", description: "Brickwork & plaster", details: "Brick masonry in cement mortar with internal and external plaster", qty: 1 },
    ],
    notes: "Quantities are estimates; final billing will be on actual measurement at site.",
    terms: [
      "Rates are based on current material prices and are valid for {validity} days.",
      "Payment: 20% mobilisation advance, running bills against work done, 5% retention released after the defect liability period.",
      "Water and electricity at site to be provided by the client free of cost.",
      "Any extra item or variation will be executed only on written approval and billed at agreed rates.",
      "Completion time will be extended for delays caused by the client, approvals, weather or force majeure.",
      "Disputes are subject to the jurisdiction of the courts where {company} is registered.",
    ],
  },
  {
    id: "interior",
    features: ["sections", "optional", "photos", "schedule"],
    schedule: [["On order confirmation", 50], ["Before installation", 40], ["On handover", 10]],
    label: "Interior design & furnishing",
    icon: "🛋️",
    desc: "Design, modular furniture, fit-outs and décor",
    subject: "Quotation for interior design and execution",
    validityDays: 15,
    template: "royal",
    items: [
      { section: "Design", description: "Interior design consultation", details: "Space planning, 3D views and material selection", qty: 1 },
      { section: "Furniture", description: "Modular kitchen", details: "BWP plywood carcass, laminate finish, soft-close hardware", qty: 1 },
      { section: "Furniture", description: "Wardrobes", details: "Hinged / sliding shutters with internal accessories", qty: 1 },
      { section: "Ceiling & lighting", optional: true, description: "False ceiling & lighting", details: "Gypsum ceiling with cove and LED lights", qty: 1 },
    ],
    notes: "Final prices depend on the materials and finishes you choose.",
    terms: [
      "Payment: 50% on order confirmation, 40% before dispatch / installation, 10% on handover.",
      "Design changes after approval of 3D views may change the cost and timeline.",
      "Civil, electrical and plumbing works are excluded unless listed.",
      "Warranty on hardware and fittings as given by the manufacturers.",
      "Prices are valid for {validity} days.",
    ],
  },
  {
    id: "trading",
    features: ["photos", "discount"],
    label: "Manufacturing & trading",
    icon: "📦",
    desc: "Supply of goods, spares and materials",
    subject: "Quotation for supply of materials",
    validityDays: 7,
    template: "corporate",
    items: [
      { description: "Item / model", details: "Make, grade, size or specification", qty: 10 },
      { description: "Packing & forwarding", details: "", qty: 1 },
    ],
    notes: "Freight to destination is extra unless shown above.",
    terms: [
      "Prices are ex-works and valid for {validity} days, subject to stock availability.",
      "Delivery: within 7–10 working days from receipt of the confirmed purchase order and advance.",
      "Payment: 100% advance / against proforma invoice unless agreed otherwise.",
      "Goods once sold will not be taken back. Transit damages to be reported within 48 hours of delivery.",
      "Warranty as per the manufacturer's terms.",
    ],
  },
  {
    id: "consulting",
    features: ["schedule"],
    schedule: [["On signing the engagement", 50], ["On completion", 50]],
    label: "Consulting & professional",
    icon: "💼",
    desc: "Advisory, accounting, legal, HR and other professional fees",
    subject: "Fee proposal for professional services",
    validityDays: 30,
    template: "elegant",
    items: [
      { description: "Initial assessment", details: "Review of documents and current position", qty: 1 },
      { description: "Professional fees – monthly", details: "Ongoing advisory and compliance support", qty: 12 },
    ],
    notes: "Out-of-pocket expenses (travel, government fees, filings) are billed at actuals.",
    terms: [
      "Fees are payable within 15 days of the invoice.",
      "Work outside the agreed scope is billed separately at our standard hourly rates.",
      "The client is responsible for the accuracy of the information provided.",
      "Either party may end the engagement with 30 days' written notice; fees for work done till then are payable.",
      "This proposal is valid for {validity} days.",
    ],
  },
  {
    id: "events",
    features: ["sections", "optional", "schedule"],
    schedule: [["Booking advance", 50], ["3 days before the event", 50]],
    label: "Events & hospitality",
    icon: "🎉",
    desc: "Event management, catering, venues and travel",
    subject: "Quotation for event management services",
    validityDays: 10,
    template: "modern",
    items: [
      { section: "Décor", description: "Venue décor & setup", details: "Stage, backdrop, lighting and floral décor", qty: 1 },
      { section: "Catering", description: "Catering (per plate)", details: "Welcome drink, starters, main course and dessert", qty: 100 },
      { section: "Décor", optional: true, description: "Sound & AV", details: "PA system, LED wall and technician", qty: 1 },
    ],
    notes: "Final guest count to be confirmed 7 days before the event.",
    terms: [
      "Booking is confirmed on receipt of a 50% advance; balance payable 3 days before the event.",
      "Cancellation within 15 days of the event: advance is non-refundable.",
      "Any damage to rented property by guests will be charged at actuals.",
      "Prices are valid for {validity} days and subject to availability on the event date.",
    ],
  },
  {
    id: "logistics",
    features: ["optional"],
    label: "Transport & logistics",
    icon: "🚚",
    desc: "Freight, courier, packers & movers and warehousing",
    subject: "Freight quotation",
    validityDays: 7,
    template: "clean",
    items: [
      { description: "Freight charges", details: "Origin → destination, vehicle type / weight", qty: 1 },
      { description: "Loading & unloading", details: "", qty: 1 },
      { optional: true, description: "Transit insurance", details: "Optional – on declared value", qty: 1 },
    ],
    notes: "Toll, parking and permit charges are extra at actuals unless included above.",
    terms: [
      "Rates are valid for {validity} days and subject to fuel price changes.",
      "Detention beyond 24 hours at loading / unloading point is chargeable per day.",
      "Goods travel at owner's risk unless insured.",
      "Payment: within 7 days of delivery against POD.",
    ],
  },
  {
    id: "maintenance",
    features: ["optional"],
    label: "Electrical, HVAC & AMC",
    icon: "🛠️",
    desc: "Installation, repairs and annual maintenance contracts",
    subject: "Quotation for annual maintenance contract (AMC)",
    validityDays: 15,
    template: "classic",
    items: [
      { description: "Annual maintenance contract", details: "4 preventive visits per year and unlimited breakdown calls", qty: 1 },
      { description: "Spare parts", details: "Charged at actuals as per replacement", qty: 1 },
    ],
    notes: "Response time for breakdown calls: within 24 hours on working days.",
    terms: [
      "AMC charges are payable in advance.",
      "Consumables and spare parts are not included unless listed.",
      "Damage due to voltage fluctuation, misuse or third-party work is not covered.",
      "The contract is valid for 12 months from the start date.",
      "Prices are valid for {validity} days.",
    ],
  },
  {
    id: "training",
    features: ["optional", "discount"],
    label: "Education & training",
    icon: "🎓",
    desc: "Courses, corporate training and workshops",
    subject: "Proposal for corporate training programme",
    validityDays: 30,
    template: "clean",
    items: [
      { description: "Training programme", details: "2-day instructor-led workshop for up to 20 participants", qty: 1 },
      { description: "Course material & certificates", details: "Printed handbook and completion certificate per participant", qty: 20 },
    ],
    notes: "Travel and stay for trainers outside the city are billed at actuals.",
    terms: [
      "50% advance to confirm dates; balance on completion of the programme.",
      "Rescheduling with less than 7 days' notice may attract a charge.",
      "Training content remains the intellectual property of {company}.",
      "This proposal is valid for {validity} days.",
    ],
  },
];

export const presetById = (id) => INDUSTRY_PRESETS.find((p) => p.id === id);

// Options switched on for a new document: what the business type needs (or the
// industry starter that was picked).
export function featureDefaults(industryId) {
  const p = presetById(industryId);
  return Object.fromEntries(Object.keys(FEATURES).map((k) => [k, !!p?.features?.includes(k)]));
}

export const presetSchedule = (preset) => (preset?.schedule || []).map(([label, percent], i) => ({ id: `s${i + 1}`, label, percent }));

// Number the terms and fill in the business name / validity.
export function presetTerms(preset, { company = "", validity } = {}) {
  const days = validity ?? preset.validityDays ?? 15;
  return preset.terms
    .map((t, i) => `${i + 1}. ${t.replaceAll("{company}", company || "us").replaceAll("{validity}", String(days))}`)
    .join("\n");
}
